<script lang="ts">
    import { base } from '$app/paths';
    import { insertIntoArray, isDark } from '$lib/helper';

    import Animation from './Animation.svelte';
    import Connector from './Connector.svelte';
    import Link from './Link.svelte';
    import Box from '../Blocks/_wrappers/Box.svelte';
    import ConsentOverlay from '../ConsentOverlay/ConsentOverlay.svelte';

    import type { IntegrationData } from '$lib/components/ConsentOverlay/types';
    import type { Box as BoxType } from '$lib/types/Components/SharedTypes';
    import type { LayoutData } from '$lib/types/LayoutData';
    import type { Theme, TransitionEffect } from '$lib/types/Theme';
    import type { ComponentType } from 'svelte';

    export let campaignId: string;
    export let campaignVersionId: string;
    export let cookieOverlay: LayoutData['cookieOverlay'] = {
        language: 'en',
        layout: 'explicit',
    };
    export let integrations: IntegrationData = {};
    export let legalLink: string;
    export let legalText: string;
    export let termsLink: string;
    export let termsText: string;
    export let claimText = 'We use Perspective';
    export let bgColorReference = '#FFFFFF';
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';
    export let branding = true;
    export let utmParams = '';
    export let language = 'en';
    export let box: BoxType;
    export let theme: Theme | undefined = undefined;

    const logo = 'https://perspective.imgix.net/assets/app/logo/32x32.png?dpr=2&auto=compress&q=75';

    //Create Links
    let linkComponents: [ComponentType, string, string][] = [];

    if (legalLink && legalText) {
        linkComponents.push([Link as ComponentType, legalText, legalLink]);
    }

    if (termsLink && termsText) {
        linkComponents.push([Link as ComponentType, termsText, termsLink]);
    }

    linkComponents = insertIntoArray(linkComponents, [Connector]);

    let cookieOverlayVisible = false;

    const showCookieOverlay = () => {
        cookieOverlayVisible = true;
    };

    const hideCookieOverlay = () => {
        cookieOverlayVisible = false;
    };

    $: hasCookies = cookieOverlay.layout !== 'none';

    const resolveMutedTextFallbackClass = (theme: Theme | undefined, bgColorReference: string) => {
        const isThemeV2 = !!theme?.mutedText;

        if (isThemeV2) {
            return '';
        }

        if (isDark(bgColorReference)) {
            return 'fun-text-white/60';
        }
        return 'fun-text-black/60';
    };

    const resolveSurfaceFallbackClass = (theme: Theme | undefined, bgColorReference: string) => {
        const isThemeV2 = !!theme?.surface;

        if (isThemeV2) {
            return '';
        }

        if (isDark(bgColorReference)) {
            return 'fun-bg-white/20';
        }
        return 'fun-bg-black/5';
    };
</script>

<Box {...box} backgroundColor={bgColorReference} {transitionDelay} {transitionEffect}>
    <div
        class="fun-flex fun-h-full fun-items-center fun-justify-center fun-font-ui fun-text-sm fun-font-normal"
    >
        <div class="fun-flex fun-flex-col fun-items-center">
            {#if linkComponents.length > 0 || hasCookies}
                <div
                    class="fun-text-center fun-text-xs {resolveMutedTextFallbackClass(
                        theme,
                        bgColorReference,
                    )}"
                    class:fun-pb-6={branding}
                    style:color={theme?.mutedText}
                >
                    {#each linkComponents as [component, text, link], index (`footer-link-${index}`)}
                        <svelte:component this={component} {text} {link} />&nbsp;
                    {/each}
                    {#if linkComponents.length > 0 && hasCookies}
                        <Connector />
                    {/if}

                    {#if cookieOverlay.layout === 'explicit'}
                        <button
                            class="fun-inline fun-cursor-pointer fun-font-ui fun-underline"
                            on:click={showCookieOverlay}
                        >
                            {language === 'de' ? 'Cookies verwalten' : 'Manage Cookies'}
                        </button>
                    {/if}

                    {#if cookieOverlay.layout === 'normal'}
                        <a
                            href={`${base}/consent-policy`}
                            class="fun-inline fun-cursor-pointer fun-font-ui fun-underline"
                        >
                            {language === 'de' ? 'Cookie Richtlinien' : 'Cookie Policy'}
                        </a>
                    {/if}
                </div>
            {/if}
            {#if branding}
                <div>
                    <a
                        href={`https://perspective.co${utmParams}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        class="fun-relative fun-flex fun-items-center fun-rounded-md fun-px-3 fun-py-2.5 fun-font-ui fun-no-underline {resolveSurfaceFallbackClass(
                            theme,
                            bgColorReference,
                        )}"
                        style:background-color={theme?.surface}
                    >
                        <Animation />
                        <img src={logo} alt="logo" class="fun-mr-3 fun-w-5" />
                        <span
                            class={resolveMutedTextFallbackClass(theme, bgColorReference)}
                            style:color={theme?.mutedText}
                        >
                            {claimText}
                        </span>
                    </a>
                </div>
            {/if}
        </div>
    </div>

    {#if cookieOverlayVisible}
        <ConsentOverlay
            layout={cookieOverlay.layout}
            language={cookieOverlay.language}
            {integrations}
            {legalLink}
            {termsLink}
            {campaignId}
            {campaignVersionId}
            forceVisible={cookieOverlayVisible}
            onAcceptClick={hideCookieOverlay}
            onCookiePoliciesLinkClick={hideCookieOverlay}
        />
    {/if}
</Box>
